import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { noteAPI, methodologyAPI, chainAPI, classroomAPI, groupAPI } from '../lib/api';
import { cacheNotes, cacheNote, getCachedNotes, removeCachedNote, enqueueMutation } from '../lib/offline';
import { useOnline } from '../context/OnlineContext';
import { Plus, Save, Trash2, Link as LinkIcon, FileText, BookOpen, Loader2, CheckCircle2, Send, Sparkles, ChevronDown, ChevronRight, X, History } from 'lucide-react';

const STEP_OPTIONS = [
  { key: '', label: '— no step —' },
  { key: 'origin', label: 'Origin' },
  { key: 'change_a', label: 'Change A' },
  { key: 'change_b', label: 'Change B' },
  { key: 'contradiction', label: 'Contradiction' },
  { key: 'modern_pattern', label: 'Modern Pattern' },
];

/* ─── Methodology rail ─── */
function MethodologyRail({ activeStepKey }) {
  const [prompts, setPrompts] = useState({ by_step: {}, global: [] });
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    methodologyAPI.prompts().then((r) => setPrompts(r.data)).catch(() => {});
  }, []);

  const stepPrompts = activeStepKey ? (prompts.by_step?.[activeStepKey] || []) : [];

  return (
    <div className="border border-[#D4A84A]/15 rounded p-3 space-y-3 bg-[#0A0A0A]/60" data-testid="methodology-rail">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between font-hud text-[10px] tracking-widest uppercase text-[#D4A84A]"
      >
        <span className="flex items-center gap-1.5"><Sparkles size={11} /> METHODOLOGY PROMPTS</span>
        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </button>
      {expanded && (
        <>
          {stepPrompts.length > 0 && (
            <div className="space-y-2" data-testid={`methodology-step-${activeStepKey}`}>
              <p className="font-hud text-[9px] tracking-wider text-[#2F8A92] uppercase">
                For: {STEP_OPTIONS.find((s) => s.key === activeStepKey)?.label}
              </p>
              {stepPrompts.map((p, i) => (
                <p key={`${activeStepKey}-prompt-${i}-${p.slice(0, 24)}`} className="font-mono text-[11px] text-[#e8d5a3] leading-relaxed italic border-l border-[#D4A84A]/30 pl-2">
                  {p}
                </p>
              ))}
            </div>
          )}
          <div className="space-y-2 pt-2 border-t border-[#2F8A92]/10" data-testid="methodology-global">
            <p className="font-hud text-[9px] tracking-wider text-[#2F8A92] uppercase">Always ask</p>
            {(prompts.global || []).slice(0, 5).map((p, i) => (
              <p key={`global-prompt-${i}-${p.slice(0, 24)}`} className="font-mono text-[10px] text-white/80 leading-relaxed">
                · {p}
              </p>
            ))}
          </div>
          <p className="font-mono text-[9px] text-[#52525B] pt-2 border-t border-[#2F8A92]/10">
            These prompts ask questions — they do not tell you what to think. Your interpretation is yours.
          </p>
        </>
      )}
    </div>
  );
}

/* ─── Citation block ─── */
function CitationBlockView({ citation, onRemove }) {
  return (
    <div className="flex gap-2 p-2 border border-[#2F8A92]/15 rounded bg-[#2F8A92]/5" data-testid={`citation-${citation.id}`}>
      {citation.image && (
        <img src={citation.image} alt="" className="w-16 h-16 object-cover rounded flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <a
          href={citation.url}
          target="_blank"
          rel="noreferrer"
          className="font-hud text-xs text-[#2F8A92] hover:text-[#66FAFF] truncate block"
        >
          {citation.title || citation.url}
        </a>
        {citation.description && (
          <p className="font-mono text-[10px] text-white/80 line-clamp-2 mt-0.5">{citation.description}</p>
        )}
        <div className="flex items-center gap-2 mt-1">
          <span className="font-mono text-[9px] text-[#52525B]">{citation.source_name || citation.url}</span>
          {citation.published && (
            <span className="font-mono text-[9px] text-[#52525B]">· {citation.published.slice(0, 10)}</span>
          )}
        </div>
      </div>
      <button
        data-testid={`remove-citation-${citation.id}`}
        onClick={onRemove}
        className="text-[#52525B] hover:text-red-400 flex-shrink-0 self-start"
      >
        <X size={14} />
      </button>
    </div>
  );
}

function CitationInput({ onAdd }) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAdd = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setError('');
    try {
      const r = await noteAPI.fetchCitation(url.trim());
      onAdd(r.data);
      setUrl('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex gap-2">
        <input
          data-testid="citation-url-input"
          className="hud-input text-xs flex-1"
          placeholder="Paste source URL…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          disabled={loading}
        />
        <button
          data-testid="add-citation-btn"
          onClick={handleAdd}
          disabled={loading || !url.trim()}
          className="font-hud text-[10px] tracking-wider text-[#2F8A92] border border-[#2F8A92]/30 px-3 py-1.5 hover:bg-[#2F8A92]/10 disabled:opacity-40 transition-all duration-300 flex items-center gap-1"
        >
          {loading ? <Loader2 size={10} className="animate-spin" /> : <LinkIcon size={10} />}
          CITE
        </button>
      </div>
      {error && <p className="text-[9px] text-red-400 font-mono">{error}</p>}
    </div>
  );
}

/* ─── Section editor ─── */
function SectionEditor({ section, onChange, onRemove, onFocus }) {
  const updateField = (field, value) => onChange({ ...section, [field]: value });
  const addCitation = (citation) => updateField('citations', [...(section.citations || []), citation]);
  const removeCitation = (cid) => updateField('citations', (section.citations || []).filter((c) => c.id !== cid));

  return (
    <div
      className="border border-[#2F8A92]/10 rounded p-3 space-y-3"
      onFocus={onFocus}
      data-testid={`section-${section.id}`}
    >
      <div className="flex items-center gap-2">
        <input
          data-testid={`section-heading-${section.id}`}
          className="hud-input text-sm font-historical flex-1"
          placeholder="Section heading…"
          value={section.heading || ''}
          onChange={(e) => updateField('heading', e.target.value)}
          onFocus={onFocus}
        />
        <select
          data-testid={`section-step-${section.id}`}
          value={section.step_key || ''}
          onChange={(e) => updateField('step_key', e.target.value)}
          className="hud-input text-xs w-36"
        >
          {STEP_OPTIONS.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
        <button
          data-testid={`remove-section-${section.id}`}
          onClick={onRemove}
          className="text-[#52525B] hover:text-red-400 transition-all duration-300"
        >
          <Trash2 size={14} />
        </button>
      </div>
      <textarea
        data-testid={`section-body-${section.id}`}
        className="hud-input min-h-[120px] resize-y text-sm leading-relaxed"
        placeholder="Write your analysis…"
        value={section.body || ''}
        onChange={(e) => updateField('body', e.target.value)}
        onFocus={onFocus}
      />
      {(section.citations || []).length > 0 && (
        <div className="space-y-1.5">
          {section.citations.map((c) => (
            <CitationBlockView key={c.id} citation={c} onRemove={() => removeCitation(c.id)} />
          ))}
        </div>
      )}
      <CitationInput onAdd={addCitation} />
    </div>
  );
}

/* ─── Note list sidebar ─── */
function NoteList({ notes, activeId, onSelect, onNew, onDelete }) {
  return (
    <div className="w-64 flex-shrink-0 border-r border-[#2F8A92]/10 flex flex-col" data-testid="note-list">
      <div className="p-3 border-b border-[#2F8A92]/10 flex items-center justify-between">
        <h3 className="font-hud text-[10px] tracking-widest uppercase text-[#2F8A92]">Notes</h3>
        <button
          data-testid="new-note-btn"
          onClick={onNew}
          className="font-hud text-[10px] tracking-wider text-[#0A0A0A] bg-[#2F8A92] px-2 py-1 hover:bg-[#66FAFF] transition-all duration-300 flex items-center gap-1"
        >
          <Plus size={10} /> NEW
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {notes.length === 0 && (
          <p className="p-4 font-mono text-[10px] text-[#52525B]">No notes yet.</p>
        )}
        {notes.map((n) => (
          <div
            key={n.id}
            className={`px-3 py-2 border-b border-[#2F8A92]/5 cursor-pointer group transition-all duration-300 ${
              n.id === activeId ? 'bg-[#2F8A92]/10' : 'hover:bg-[#2F8A92]/5'
            }`}
            onClick={() => onSelect(n.id)}
            data-testid={`note-item-${n.id}`}
          >
            <div className="flex items-start justify-between gap-1">
              <div className="flex-1 min-w-0">
                <p className="font-historical text-xs text-[#D4A84A] truncate">{n.title || 'Untitled'}</p>
                <p className="font-mono text-[9px] text-[#52525B] mt-0.5">
                  {new Date(n.updated_at).toLocaleDateString()} · v{n.version || 1}
                </p>
              </div>
              <button
                data-testid={`delete-note-${n.id}`}
                onClick={(e) => { e.stopPropagation(); onDelete(n.id); }}
                className="text-[#52525B] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
              >
                <Trash2 size={10} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Chain reference panel ─── */
function ChainReference({ chainId, onPickChain, chains }) {
  const [expanded, setExpanded] = useState(false);
  const chain = chains.find((c) => c.id === chainId);

  return (
    <div className="border border-[#D4A84A]/15 rounded" data-testid="chain-reference">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 py-2 flex items-center justify-between font-hud text-[10px] tracking-widest uppercase text-[#D4A84A]"
      >
        <span className="flex items-center gap-1.5"><BookOpen size={11} /> CHAIN REFERENCE</span>
        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </button>
      {expanded && (
        <div className="p-3 border-t border-[#D4A84A]/10 space-y-2">
          <select
            data-testid="chain-picker"
            value={chainId || ''}
            onChange={(e) => onPickChain(e.target.value || null)}
            className="hud-input text-xs w-full"
          >
            <option value="">— Link a chain —</option>
            {chains.map((c) => (
              <option key={c.id} value={c.id}>{c.subject}</option>
            ))}
          </select>
          {chain && (
            <div className="space-y-1 text-[10px] font-mono">
              <p className="text-white/80">{chain.description}</p>
              {['origin', 'change_a', 'change_b', 'contradiction', 'modern_pattern'].map((k) => (
                chain[k]?.title && (
                  <p key={k} className="text-[#52525B]">
                    <span className="text-[#2F8A92]">{k}:</span> {chain[k].title}
                  </p>
                )
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Export picker ─── */
function ExportMenu({ noteId }) {
  const [groups, setGroups] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    if (!open) return;
    groupAPI.list().then((r) => setGroups(r.data)).catch(() => setGroups([]));
    classroomAPI.list().then((r) => setClassrooms(r.data.filter((c) => c.role === 'student'))).catch(() => setClassrooms([]));
  }, [open]);

  const sendToGroup = async (gid) => {
    try {
      await noteAPI.sendToGroup(noteId, gid);
      setStatus('Sent to group.');
      setTimeout(() => { setStatus(''); setOpen(false); }, 1500);
    } catch {
      setStatus('Failed.');
    }
  };

  if (groups.length === 0 && classrooms.length === 0 && !open) return null;

  return (
    <div className="relative" data-testid="export-menu">
      <button
        data-testid="export-menu-btn"
        onClick={() => setOpen(!open)}
        className="font-hud text-[10px] tracking-wider text-[#D4A84A] border border-[#D4A84A]/30 px-3 py-1.5 hover:bg-[#D4A84A]/5 transition-all duration-300 flex items-center gap-1"
      >
        <Send size={10} /> EXPORT
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 border border-[#D4A84A]/20 bg-[#0A0A0A] p-2 z-10 rounded shadow-xl space-y-1">
          {status && <p className="font-mono text-[10px] text-[#2F8A92]">{status}</p>}
          {groups.length > 0 && (
            <>
              <p className="font-hud text-[9px] tracking-wider uppercase text-[#52525B] px-1">To group</p>
              {groups.map((g) => (
                <button
                  key={g.id}
                  data-testid={`send-to-group-${g.id}`}
                  onClick={() => sendToGroup(g.id)}
                  className="w-full text-left px-2 py-1 font-mono text-[11px] text-white/80 hover:bg-[#D4A84A]/5 hover:text-[#D4A84A] rounded transition-all"
                >
                  {g.name}
                </button>
              ))}
            </>
          )}
          {groups.length === 0 && classrooms.length === 0 && (
            <p className="font-mono text-[10px] text-[#52525B] px-1">
              Join a group or classroom to export. Use the Classroom tab to submit assignments.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Main Notepad (Mode A) ─── */
export default function Notepad() {
  const [notes, setNotes] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [title, setTitle] = useState('');
  const [sections, setSections] = useState([]);
  const [chainId, setChainId] = useState(null);
  const [chains, setChains] = useState([]);
  const [activeStepKey, setActiveStepKey] = useState('');
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved
  const [showHistory, setShowHistory] = useState(false);
  const dirtyRef = useRef(false);
  const autosaveTimer = useRef(null);

  // Load notes + chains — IDB-first, network-refresh
  const loadNotes = useCallback(() => {
    // Fast path: show cached notes immediately
    getCachedNotes().then((cached) => {
      if (cached?.length && notes.length === 0) setNotes(cached);
    }).catch(() => {});
    // Network refresh
    noteAPI.list()
      .then((r) => {
        setNotes(r.data);
        cacheNotes(r.data).catch(() => {});
        if (!activeId && r.data.length > 0) {
          loadNote(r.data[0].id);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, notes.length]);

  const { online } = useOnline();

  useEffect(() => {
    loadNotes();
    chainAPI.list().then((r) => setChains(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When connection returns, sync is flushed automatically by OnlineProvider.
  // After that completes (brief delay), refresh the notes list once.
  useEffect(() => {
    if (online) {
      const t = setTimeout(() => loadNotes(), 1500);
      return () => clearTimeout(t);
    }
  }, [online, loadNotes]);

  const loadNote = async (id) => {
    try {
      const r = await noteAPI.get(id);
      setActiveId(r.data.id);
      setTitle(r.data.title || '');
      setSections(r.data.sections || []);
      setChainId(r.data.chain_id || null);
      dirtyRef.current = false;
      setSaveState('saved');
    } catch {}
  };

  const handleNewNote = async () => {
    const res = await noteAPI.create({ title: 'New note', sections: [] });
    await loadNotes();
    loadNote(res.data.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete note?')) return;
    await noteAPI.delete(id);
    if (activeId === id) {
      setActiveId(null);
      setTitle('');
      setSections([]);
    }
    loadNotes();
  };

  const addSection = () => {
    const newSec = { id: `tmp-${Date.now()}`, heading: '', step_key: '', body: '', citations: [] };
    setSections([...sections, newSec]);
    dirtyRef.current = true;
  };

  const updateSection = (sec) => {
    setSections(sections.map((s) => (s.id === sec.id ? sec : s)));
    dirtyRef.current = true;
  };

  const removeSection = (id) => {
    setSections(sections.filter((s) => s.id !== id));
    dirtyRef.current = true;
  };

  // Auto-save every 10 seconds when dirty.
  // Methodology: IDB is the source of truth. Network is a sync target.
  useEffect(() => {
    if (!activeId) return;
    const tick = async () => {
      if (!dirtyRef.current) return;
      const payload = { title, sections, chain_id: chainId };
      // 1) Cache the in-flight version locally — always, before anything else
      try {
        await cacheNote({ id: activeId, ...payload, updated_at: new Date().toISOString() });
      } catch {}
      setSaveState('saving');
      // 2) Try network. If offline (or it fails), enqueue for later sync.
      if (navigator.onLine) {
        try {
          const res = await noteAPI.autosave(activeId, payload);
          dirtyRef.current = false;
          setSaveState('saved');
          if (res.data?.sections) {
            setSections(res.data.sections);
            // Update IDB with server-authoritative UUIDs
            await cacheNote({ id: activeId, ...payload, sections: res.data.sections, updated_at: new Date().toISOString() }).catch(() => {});
          }
          noteAPI.list().then((r) => { setNotes(r.data); cacheNotes(r.data).catch(() => {}); }).catch(() => {});
          return;
        } catch {
          // Fall through to queue
        }
      }
      try {
        await enqueueMutation({
          method: 'POST',
          path: `/api/notes/${activeId}/autosave`,
          body: payload,
          kind: 'note_autosave',
        });
        dirtyRef.current = false;
        setSaveState('saved');
      } catch {
        setSaveState('idle');
      }
    };
    autosaveTimer.current = setInterval(tick, 10000);
    return () => clearInterval(autosaveTimer.current);
  }, [activeId, title, sections, chainId]);

  const handleSaveNow = async () => {
    if (!activeId) return;
    setSaveState('saving');
    const payload = { title, sections, chain_id: chainId };
    try { await cacheNote({ id: activeId, ...payload, updated_at: new Date().toISOString() }); } catch {}
    if (navigator.onLine) {
      try {
        const res = await noteAPI.autosave(activeId, payload);
        if (res.data?.sections) setSections(res.data.sections);
        dirtyRef.current = false;
        setSaveState('saved');
        return;
      } catch {
        // fallthrough
      }
    }
    try {
      await enqueueMutation({
        method: 'POST',
        path: `/api/notes/${activeId}/autosave`,
        body: payload,
        kind: 'note_autosave',
      });
      dirtyRef.current = false;
      setSaveState('saved');
    } catch {
      setSaveState('idle');
    }
  };

  const markDirty = (setter) => (val) => {
    dirtyRef.current = true;
    setter(val);
  };

  return (
    <div className="flex h-[calc(100vh-280px)] md:h-[calc(100vh-240px)] border border-[#2F8A92]/10 rounded overflow-hidden" data-testid="notepad-mode-a">
      <NoteList
        notes={notes}
        activeId={activeId}
        onSelect={loadNote}
        onNew={handleNewNote}
        onDelete={handleDelete}
      />

      {activeId ? (
        <div className="flex-1 flex overflow-hidden">
          {/* Editor column */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {/* Title + save state */}
            <div className="flex items-center gap-3">
              <input
                data-testid="note-title"
                className="hud-input text-lg font-historical text-[#D4A84A] flex-1"
                placeholder="Note title"
                value={title}
                onChange={(e) => markDirty(setTitle)(e.target.value)}
              />
              <div className="font-hud text-[10px] tracking-wider flex items-center gap-1.5" data-testid="save-state">
                {saveState === 'saving' && <><Loader2 size={10} className="animate-spin text-[#2F8A92]" /><span className="text-[#2F8A92]">SAVING</span></>}
                {saveState === 'saved' && <><CheckCircle2 size={10} className="text-[#52525B]" /><span className="text-[#52525B]">SAVED</span></>}
                {saveState === 'idle' && dirtyRef.current && <span className="text-[#D4A84A]">UNSAVED</span>}
              </div>
              <button
                data-testid="save-now-btn"
                onClick={handleSaveNow}
                className="font-hud text-[10px] tracking-wider text-[#0A0A0A] bg-[#2F8A92] px-2 py-1 hover:bg-[#66FAFF]"
              >
                <Save size={10} />
              </button>
              <button
                data-testid="history-btn"
                onClick={() => setShowHistory(true)}
                title="Version history"
                className="font-hud text-[10px] tracking-wider text-white/80 border border-[#52525B]/30 px-2 py-1 hover:text-[#D4A84A] hover:border-[#D4A84A]/40"
              >
                <History size={10} />
              </button>
              <ExportMenu noteId={activeId} />
            </div>

            <ChainReference chainId={chainId} onPickChain={markDirty(setChainId)} chains={chains} />

            {/* Sections */}
            {sections.map((s) => (
              <SectionEditor
                key={s.id}
                section={s}
                onChange={updateSection}
                onRemove={() => removeSection(s.id)}
                onFocus={() => setActiveStepKey(s.step_key || '')}
              />
            ))}
            <button
              data-testid="add-section-btn"
              onClick={addSection}
              className="w-full flex items-center justify-center gap-2 px-3 py-3 border border-dashed border-[#2F8A92]/25 rounded font-hud text-[10px] tracking-wider text-[#2F8A92] hover:bg-[#2F8A92]/5 transition-all duration-300"
            >
              <Plus size={12} /> ADD SECTION
            </button>
            {sections.length === 0 && (
              <p className="font-mono text-[11px] text-[#52525B] text-center py-4">
                Add a section to begin. Sections can be tagged with a chain step to activate methodology prompts.
              </p>
            )}
          </div>

          {/* Methodology rail */}
          <div className="w-72 flex-shrink-0 border-l border-[#2F8A92]/10 p-3 overflow-y-auto">
            <MethodologyRail activeStepKey={activeStepKey} />
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-2">
            <FileText size={32} className="mx-auto text-[#52525B]" />
            <p className="font-mono text-sm text-[#52525B]">Select or create a note to begin.</p>
            <button
              onClick={handleNewNote}
              className="font-hud text-[10px] tracking-wider text-[#0A0A0A] bg-[#2F8A92] px-3 py-1.5 hover:bg-[#66FAFF]"
            >
              + START A NEW NOTE
            </button>
          </div>
        </div>
      )}
      {showHistory && activeId && (
        <VersionHistoryModal
          noteId={activeId}
          currentTitle={title}
          onClose={() => setShowHistory(false)}
          onRestore={(v) => {
            setTitle(v.title || '');
            setSections(Array.isArray(v.sections) ? v.sections : []);
            setChainId(v.chain_id || null);
            dirtyRef.current = true;
            setShowHistory(false);
            setSaveState('idle');
          }}
        />
      )}
    </div>
  );
}

function VersionHistoryModal({ noteId, currentTitle, onClose, onRestore }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    setLoading(true);
    noteAPI.versions(noteId)
      .then((r) => setVersions(r.data || []))
      .catch((e) => setError(e.response?.data?.detail || 'Failed to load history.'))
      .finally(() => setLoading(false));
  }, [noteId]);

  // Portal to document.body so HUD stacking contexts can't occlude the modal.
  const modalBody = (
    <div
      className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ zIndex: 10050, isolation: 'isolate' }}
      onClick={onClose}
      data-testid="version-history-modal"
    >
      <div
        className="bg-[#0A0A0A] border border-[#2F8A92]/30 rounded-lg max-w-3xl w-full p-5 md:p-6 space-y-4 shadow-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-historical text-[#D4A84A] text-xl font-light tracking-tight flex items-center gap-2">
              <History size={18} /> Version history
            </h3>
            <p className="font-mono text-[10px] text-white/80">
              Every content change to <span className="text-[#D4A84A]">{currentTitle || 'this note'}</span> snapshots the prior state here.
              Restoring creates a new draft — it doesn't delete any version.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="version-history-close"
            aria-label="Close version history"
            className="text-white/80 hover:text-[#D4A84A] p-1 -m-1"
          >
            <X size={16} />
          </button>
        </div>

        {loading ? (
          <div className="font-hud text-[11px] text-[#2F8A92] tracking-widest animate-pulse">LOADING HISTORY…</div>
        ) : error ? (
          <div className="font-mono text-[11px] text-red-400">{error}</div>
        ) : versions.length === 0 ? (
          <div className="space-y-1" data-testid="version-history-empty">
            <p className="font-historical text-[#D4A84A] text-base font-light">No prior versions yet.</p>
            <p className="font-mono text-[11px] text-[#52525B]">The next time your content changes and saves, a snapshot will appear here.</p>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex gap-4">
            {/* Version list */}
            <div className="w-56 flex-shrink-0 border border-[#52525B]/15 rounded overflow-y-auto" data-testid="version-list">
              {versions.map((v) => (
                <button
                  key={v.id}
                  data-testid={`version-row-${v.version}`}
                  onClick={() => setPreview(v)}
                  className={`w-full text-left p-2 border-b border-[#52525B]/10 transition-all ${
                    preview?.id === v.id ? 'bg-[#2F8A92]/10 border-l-2 border-l-[#2F8A92]' : 'hover:bg-[#2F8A92]/5'
                  }`}
                >
                  <p className="font-hud text-[10px] tracking-wider text-[#2F8A92]">v{v.version}</p>
                  <p className="font-mono text-[10px] text-white/80 truncate">{v.title || '(untitled)'}</p>
                  <p className="font-mono text-[9px] text-[#52525B]">
                    {v.created_at ? new Date(v.created_at).toLocaleString() : ''}
                  </p>
                </button>
              ))}
            </div>

            {/* Preview */}
            <div className="flex-1 overflow-y-auto border border-[#52525B]/15 rounded p-3">
              {preview ? (
                <div className="space-y-3" data-testid="version-preview">
                  <div>
                    <p className="font-hud text-[10px] tracking-wider text-white/80 uppercase">TITLE</p>
                    <p className="font-historical text-[#D4A84A] text-lg font-light">{preview.title || '(untitled)'}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="font-hud text-[10px] tracking-wider text-white/80 uppercase">SECTIONS</p>
                    {(preview.sections || []).map((s, i) => (
                      <div key={s.id || i} className="border border-[#52525B]/15 rounded p-2">
                        {s.step_key && <p className="font-hud text-[9px] tracking-wider text-[#2F8A92] uppercase mb-1">{s.step_key}</p>}
                        {s.type === 'text' && <p className="font-mono text-[11px] text-white/80 whitespace-pre-wrap">{s.text || ''}</p>}
                        {s.type === 'citation' && <p className="font-mono text-[10px] text-[#66FAFF] break-all">{s.url || ''} — {s.title || ''}</p>}
                        {!['text', 'citation'].includes(s.type) && <p className="font-mono text-[10px] text-[#52525B]">[{s.type}]</p>}
                      </div>
                    ))}
                  </div>
                  <button
                    data-testid="version-restore-btn"
                    onClick={() => onRestore(preview)}
                    className="font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-3 py-2 hover:bg-[#D4A84A]/85"
                  >
                    RESTORE THIS VERSION
                  </button>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center font-mono text-[11px] text-[#52525B]">
                  Select a version to preview.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Escape HUD stacking contexts — render at the document.body root so
  // nothing in the fixed HUD chrome can paint above us.
  if (typeof document === 'undefined') return modalBody;
  return createPortal(modalBody, document.body);
}
